import get from "lodash/get";
import { Field, FieldProps, FormikErrors, FormikTouched, getIn } from "formik";
import { useParamSelector } from "@hooks/use-param-selector";
import { shouldHighlightInput } from "@redux/selectors/verification-form-selectors";
import { GenericAttributeContainer } from "../generic-attribute-container/generic-attribute-container";
import { AttributeTypes } from "@iddataweb/idw-common-ui/enums";
import { CustomAttributeContainer } from "../custom-attribute-container/custom-attribute-container";
import { AttributeFormState } from "@idw-types/state/attribute-form-state";
import { useSelector } from "react-redux";
import { getLanguageContent } from "@redux/selectors/language-selectors";

type AttributeContainerProps = {
    index: number;
    localizeStrings: boolean;
    touched: FormikTouched<any>;
    errors: FormikErrors<any>;
    pinFieldShouldBeAutofocused: boolean;
    attributeFormState: AttributeFormState;
    attributeValuesPinIndex: number;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    handleAddNewSubmitValue: (payload: { attribute: AttributeFormState; inputData: any }) => void;
    handleUpdateSubmitValue: (payload: { attribute: AttributeFormState; inputData: any }) => void;
    handleUpdatePickerValue: (payload: { attribute: AttributeFormState; pickerData: any }) => void;
    isSubmitting: boolean;
};

export const AttributeContainer = (props: AttributeContainerProps) => {
    const {
        index,
        localizeStrings,
        touched,
        errors,
        attributeValuesPinIndex,
        pinFieldShouldBeAutofocused,
        setFieldValue,
        setFieldTouched,
        attributeFormState,
        handleAddNewSubmitValue,
        handleUpdateSubmitValue,
        handleUpdatePickerValue,
        isSubmitting
    } = props;

    const content = useSelector(getLanguageContent);
    const fieldShouldBeHighlighted = useParamSelector(shouldHighlightInput, attributeFormState);

    const renderGenericAttributeContainer = (fieldProps: FieldProps<any>) => {
        const { field: { name, onBlur } } = fieldProps;

        const fieldTouched = getIn(touched, name);
        const fieldError = getIn(errors, name);
        const fieldIsInvalid = !!fieldError && !!fieldTouched && !attributeFormState.inBandPinMode;
        const fieldErrorMessage = fieldError ? (get(content, fieldError) || fieldError) : null;

        return (
            <GenericAttributeContainer
                localizeStrings={localizeStrings}
                fieldName={name}
                fieldTouched={fieldTouched}
                fieldError={fieldErrorMessage}
                fieldIsInvalid={fieldIsInvalid}
                fieldShouldBeHighlighted={fieldShouldBeHighlighted}
                pinFieldShouldBeAutofocused={pinFieldShouldBeAutofocused}
                attributeValuesPinIndex={attributeValuesPinIndex}
                onBlur={onBlur}
                setFieldTouched={setFieldTouched}
                setFieldValue={setFieldValue}
                attribute={attributeFormState}
                handleAddNewSubmitValue={handleAddNewSubmitValue}
                handleUpdateSubmitValue={handleUpdateSubmitValue}
                handleUpdatePickerValue={handleUpdatePickerValue}
                isSubmitting={isSubmitting}
            />
        );
    };

    const renderCustomAttributeContainer = (fieldProps: FieldProps<any>) => {
        const { field: { name, onBlur } } = fieldProps;

        return (
            <CustomAttributeContainer
                onBlur={onBlur}
                setFieldTouched={setFieldTouched}
                setFieldValue={setFieldValue}
                attribute={attributeFormState}
                fieldName={name}
                handleAddNewSubmitValue={handleAddNewSubmitValue}
                handleUpdateSubmitValue={handleUpdateSubmitValue}
                handleUpdatePickerValue={handleUpdatePickerValue}
                isSubmitting={isSubmitting}
            />
        );
    };

    return (
        <Field key={index} name={attributeFormState.type}>
            {attributeFormState.templateName === AttributeTypes.CUSTOM
                ? renderCustomAttributeContainer
                : renderGenericAttributeContainer
            }
        </Field>
    );
};